import { Controller } from "@hotwired/stimulus"

// Seconds on the tour's own clock, which stops while the map is offscreen or
// the tab is hidden.
const LEAD_IN = 0.6
const LIGHT_PASS = 3.4
const HOLD = 1
// A flight lasts in proportion to its zoom-and-pan length, so a long hop
// travels no faster than a short one.
const FLIGHT_SECONDS_PER_LENGTH = 1.4
const FLIGHT_SHORTEST = 2.4
const FLIGHT_LONGEST = 4
// How far into a flight the sheet turns to the market the camera is heading for.
const SHEET_TURN = 0.7
const HOMECOMING = 0.9
// How long the tour waits for the cities, zips and neighborhoods before its
// first light pass goes ahead without them.
const DETAIL_WAIT = 2.5
// How far from a tap, in pixels, the nearest county still answers it.
const TAP_REACH = 16
const TAP_RING = Array.from({ length: 12 }, (_, index) => index * Math.PI / 6)
// van Wijk and Nuij's curvature for a smooth zoom-and-pan.
const RHO = Math.SQRT2
const STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

// Connects to data-controller="public--territory-tour"
//
// The SCWMLS hero. The server draws the first frame, the whole MLS over its
// counties beside its sheet of numbers, which no JavaScript, reduced motion
// and a Turbo cache restore all show. The cities, zips and neighborhoods
// follow as a cached asset, and the first light pass draws them in while the
// market count climbs. Then the camera (the map's viewBox, flown along one
// zoom-and-pan curve per flight) tours the stops, each with its sheet. A
// county pointed at, tapped or focused ends the tour for good: the camera
// comes home and every county answers with its own numbers.
export default class extends Controller {
  static targets = ["map", "highlight", "sheet", "count", "hint"]
  static values = { stops: Array, detail: String }

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.drawnViewBox = this.mapTarget.getAttribute("viewBox")
    this.drawnCount = this.countTarget.textContent
    this.counties = [...this.mapTarget.querySelectorAll("#territory-counties > path")]
    this.reset = this.reset.bind(this)
    document.addEventListener("turbo:before-cache", this.reset)
    this.makeChoosable()

    this.motion = window.matchMedia("(prefers-reduced-motion: no-preference)")
    this.motionChanged = () => { if (!this.motion.matches) this.takeOver() }
    this.motion.addEventListener("change", this.motionChanged)
    this.stopped = !this.motion.matches
    if (this.stopped) this.showCount(this.drawnCount)

    const detailLoaded = this.loadDetail().then(detail => this.arrive(detail))
    if (!this.stopped) this.start(detailLoaded)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.reset)
    this.motion?.removeEventListener("change", this.motionChanged)
    this.reset()
  }

  // The counties become a radio group: one tab stop, arrow keys between
  // them, and each one's numbers as its description.
  makeChoosable() {
    this.mapTarget.removeAttribute("aria-hidden")
    this.mapTarget.setAttribute("role", "group")
    this.mapTarget.setAttribute("aria-label", "Map of the SCWMLS territory")
    const group = this.mapTarget.querySelector("#territory-counties")
    group.setAttribute("role", "radiogroup")
    group.setAttribute("aria-label", "SCWMLS counties")
    this.counties.forEach((county, index) => {
      const summary = document.getElementById(`${county.id}-summary`)
      county.setAttribute("role", "radio")
      county.setAttribute("aria-checked", "false")
      county.setAttribute("aria-label", summary.dataset.name)
      county.setAttribute("aria-describedby", summary.id)
      county.tabIndex = index === 0 ? 0 : -1
    })
    this.mapTarget.classList.add("is-choosable")
    // Without a pull, a county has a name but no numbers to point to.
    if (this.hasHintTarget) this.hintTarget.hidden = false
  }

  // A Turbo restore brings the detail back with the page.
  async loadDetail() {
    if (this.mapTarget.querySelector('[data-kind="city"]')) return null
    try {
      const response = await fetch(this.detailValue)
      if (!response.ok) return null
      return new DOMParser().parseFromString(await response.text(), "image/svg+xml").documentElement
    } catch {
      return null
    }
  }

  arrive(detail) {
    if (!detail) return
    if (this.stopped || this.lit) {
      this.insertDetail(detail)
    } else {
      this.pendingDetail = detail
    }
  }

  // Under the counties, so their lines stay on top and a pointer finds them.
  insertDetail(detail) {
    const counties = this.mapTarget.querySelector("#territory-counties")
    for (const group of [...detail.children]) counties.before(document.importNode(group, true))
  }

  start(detailLoaded) {
    this.clock = -LEAD_IN
    this.timeline = null
    this.segment = null
    this.resizeObserver = new ResizeObserver(() => this.measure())
    this.resizeObserver.observe(this.mapTarget)
    this.measure()

    this.inView = false
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting
      this.resume()
    })
    this.intersectionObserver.observe(this.element)
    this.visibilityChanged = () => this.resume()
    document.addEventListener("visibilitychange", this.visibilityChanged)

    const detailWait = new Promise(resolve => setTimeout(resolve, DETAIL_WAIT * 1000))
    Promise.race([detailLoaded, detailWait]).then(() => {
      this.ready = true
      this.resume()
    })
  }

  reset() {
    this.pause()
    cancelAnimationFrame(this.homecoming)
    this.homecoming = null
    this.stopWatching()
    if (!this.drawnViewBox) return

    this.mapTarget.setAttribute("viewBox", this.drawnViewBox)
    this.viewBox = null
    this.mapTarget.classList.remove("is-lighting", "is-arriving")
    this.element.classList.remove("is-exploring")
    this.highlight(null)
    this.turnSheet(this.stopsValue[0].id)
    this.countTarget.textContent = this.drawnCount
    this.countTarget.classList.remove("is-ready")
  }

  stopWatching() {
    this.intersectionObserver?.disconnect()
    this.resizeObserver?.disconnect()
    if (this.visibilityChanged) document.removeEventListener("visibilitychange", this.visibilityChanged)
    this.intersectionObserver = this.resizeObserver = this.visibilityChanged = null
  }

  resume() {
    if (this.ready && !this.stopped && this.inView && document.visibilityState === "visible") {
      if (this.frame) return
      this.last = null
      this.frame = requestAnimationFrame(now => this.tick(now))
    } else {
      this.pause()
    }
  }

  pause() {
    cancelAnimationFrame(this.frame)
    this.frame = null
  }

  tick(now) {
    if (this.last !== null) this.clock += Math.min((now - this.last) / 1000, 0.1)
    this.last = now
    if (this.timeline) this.play(this.clock)
    this.frame = requestAnimationFrame(next => this.tick(next))
  }

  // A mouse or pen; a finger is left to scroll the page. While the camera
  // flies home the county under the pointer is still moving, so the pick
  // waits for it to land.
  point(event) {
    if (event.pointerType === "touch") return
    this.pointer = event.target === this.mapTarget ? { x: event.clientX, y: event.clientY } : null
    if (!this.homecoming) this.pointAt(this.pointer)
  }

  pointAt(pointer) {
    const county = pointer && this.countyNear(pointer)
    this.mapTarget.classList.toggle("is-pointing", Boolean(county))
    if (!county) return
    this.takeOver()
    if (!this.homecoming) this.choose(county)
  }

  leave() {
    this.pointer = null
    this.mapTarget.classList.remove("is-pointing")
  }

  pick(event) {
    if (event.target !== this.mapTarget) return
    const county = this.countyNear({ x: event.clientX, y: event.clientY }, TAP_REACH)
    if (county) this.choose(county)
  }

  focus(event) {
    const county = this.focusedCounty(event)
    if (county) this.choose(county)
  }

  step(event) {
    const county = this.focusedCounty(event)
    if (!county) return
    // Focus has already checked the county; Space would only scroll the page.
    if (event.key === " ") event.preventDefault()
    const next = this.neighbor(county, event.key)
    if (!next) return
    event.preventDefault()
    next.focus()
  }

  // The county under a point on screen or, within reach, the nearest one.
  // By geometry, not by hit test: nothing on the map takes the pointer,
  // because WebKit lets the copies drawn over the counties take it instead.
  countyNear({ x, y }, reach = 0) {
    const toMap = this.mapTarget.getScreenCTM().inverse()
    for (let radius = 0; radius <= reach; radius += 4) {
      for (const angle of radius ? TAP_RING : [0]) {
        const point = new DOMPoint(x + radius * Math.cos(angle), y + radius * Math.sin(angle)).matrixTransform(toMap)
        const county = this.counties.find(candidate => candidate.isPointInFill(point))
        if (county) return county
      }
    }
    return null
  }

  focusedCounty(event) {
    return event.target.closest("#territory-counties > path")
  }

  // The counties are in alphabetical order; Home and End go to either end.
  neighbor(county, key) {
    const count = this.counties.length
    if (key === "Home") return this.counties[0]
    if (key === "End") return this.counties[count - 1]
    if (!(key in STEPS)) return null
    return this.counties[(this.counties.indexOf(county) + STEPS[key] + count) % count]
  }

  choose(county) {
    this.takeOver()
    if (this.chosen === county) return
    this.chosen = county
    for (const path of this.counties) {
      path.setAttribute("aria-checked", path === county)
      path.tabIndex = path === county ? 0 : -1
    }
    this.highlight(county.id)
    this.turnSheet(county.id)
  }

  takeOver() {
    if (this.stopped) return
    this.stopped = true
    this.pause()
    this.stopWatching()
    this.element.classList.add("is-exploring")
    if (this.pendingDetail) this.insertDetail(this.pendingDetail)
    this.pendingDetail = null
    this.showCount(this.drawnCount)
    this.comeHome()
  }

  comeHome() {
    const path = this.camera && zoomAndPan(this.camera, this.views[0])
    if (!path || path.length < 0.01 || !this.motion.matches) {
      this.mapTarget.setAttribute("viewBox", this.drawnViewBox)
      return
    }
    const startedAt = performance.now()
    const step = now => {
      const progress = Math.min((now - startedAt) / (HOMECOMING * 1000), 1)
      if (progress < 1) {
        // The exponential ease-out that --public-ease draws in CSS.
        this.view(path.at(1 - 2 ** (-10 * progress)))
        this.homecoming = requestAnimationFrame(step)
      } else {
        this.mapTarget.setAttribute("viewBox", this.drawnViewBox)
        this.homecoming = null
        this.pointAt(this.pointer)
      }
    }
    this.homecoming = requestAnimationFrame(step)
  }

  // The map's box decides each stop's view, so a resize re-fits them all; the
  // timings stay as first measured so the tour keeps its place.
  measure() {
    const { width, height } = this.mapTarget.getBoundingClientRect()
    if (!width || !height) return

    this.box = { width, height }
    this.views = this.stopsValue.map(stop => this.fit(stop.frame))
    const flights = this.views.map((view, index) => zoomAndPan(view, this.views[(index + 1) % this.views.length]))
    this.timeline ||= this.schedule(flights)
    this.timeline.forEach(segment => { if (segment.kind === "flight") segment.path = flights[segment.from] })
    if (this.segment) this.view(this.cameraAt(this.segment, this.progress))
  }

  schedule(flights) {
    const segments = [{ kind: "light", stop: 0, duration: LIGHT_PASS }, { kind: "hold", stop: 0, duration: HOLD }]
    flights.forEach((path, from) => {
      const to = (from + 1) % flights.length
      const duration = Math.min(Math.max(path.length * FLIGHT_SECONDS_PER_LENGTH, FLIGHT_SHORTEST), FLIGHT_LONGEST)
      segments.push({ kind: "flight", from, to, path, duration })
      if (to !== 0) segments.push({ kind: "hold", stop: to, duration: HOLD })
    })
    let start = 0
    segments.forEach(segment => { segment.start = start; start += segment.duration })
    this.loop = start
    return segments
  }

  play(clock) {
    if (clock < 0) return

    const time = clock % this.loop
    const segment = this.timeline.findLast(candidate => candidate.start <= time)
    this.progress = (time - segment.start) / segment.duration
    if (segment !== this.segment) this.enter(segment)
    this.segment = segment

    if (segment.kind === "flight") {
      this.view(this.cameraAt(segment, this.progress))
      if (this.progress >= SHEET_TURN) this.turnSheet(this.stopsValue[segment.to].id)
    }
    if (segment.kind === "light" && this.arrivals) this.countUp(time - segment.start)
  }

  enter(segment) {
    if (segment.kind === "flight") {
      this.highlight(null)
    } else {
      this.view(this.views[segment.stop])
      this.turnSheet(this.stopsValue[segment.stop].id)
      // Home is the whole territory, left unlit.
      this.highlight(segment.stop === 0 ? null : this.stopsValue[segment.stop].id)
    }
    if (segment.kind === "light") this.lightUp()
  }

  // The first pass draws the detail in; every pass traces the counties and
  // brightens the markets in a ripple out from Madison.
  lightUp() {
    this.mapTarget.classList.remove("is-lighting", "is-arriving")
    void this.mapTarget.getBoundingClientRect()
    if (this.pendingDetail) {
      this.insertDetail(this.pendingDetail)
      this.pendingDetail = null
      this.arrivals = this.arrivalTimes()
      this.mapTarget.classList.add("is-arriving")
    } else {
      this.arrivals = null
      this.showCount(this.drawnCount)
    }
    this.lit = true
    this.mapTarget.classList.add("is-lighting")
  }

  cameraAt(segment, progress) {
    if (segment.kind !== "flight") return this.views[segment.stop]
    return segment.path.at((1 - Math.cos(Math.PI * progress)) / 2)
  }

  // A stop's frame, fitted inside the map's box the way the server's
  // preserveAspectRatio fits the first one: [centre x, centre y, visible width].
  fit([x, y, width, height]) {
    const scale = Math.min(this.box.width / width, this.box.height / height)
    return [x + width / 2, y + height / 2, this.box.width / scale]
  }

  view(camera) {
    const [x, y, width] = camera
    const height = width * this.box.height / this.box.width
    const viewBox = `${(x - width / 2).toFixed(1)} ${(y - height / 2).toFixed(1)} ${width.toFixed(1)} ${height.toFixed(1)}`
    if (viewBox !== this.viewBox) this.mapTarget.setAttribute("viewBox", viewBox)
    this.viewBox = viewBox
    this.camera = camera
  }

  countUp(elapsed) {
    const arrived = this.arrivals.findIndex(time => time > elapsed)
    const total = Number(this.drawnCount)
    this.showCount(arrived === -1 ? total : total - this.arrivals.length + arrived)
  }

  // The numeral stays hidden until the tour writes its first value, so the
  // server's total never flashes before the count starts low.
  showCount(count) {
    this.countTarget.textContent = Number(count).toLocaleString("en-US")
    this.countTarget.classList.add("is-ready")
  }

  // When each detail market starts to arrive: its --d of the spread in CSS.
  arrivalTimes() {
    const spread = parseFloat(getComputedStyle(this.mapTarget).getPropertyValue("--cascade-spread"))
    return [...this.mapTarget.querySelectorAll("path[style]")]
      .map(path => parseFloat(path.style.getPropertyValue("--d")) * spread)
      .sort((a, b) => a - b)
  }

  // Unlit, the highlight keeps its market so it fades out where it was.
  highlight(id) {
    if (id) this.highlightTarget.setAttribute("href", `#${id}`)
    this.highlightTarget.classList.toggle("is-shown", Boolean(id))
  }

  turnSheet(id) {
    if (this.sheet === id) return
    this.sheetTargets.forEach(sheet => sheet.classList.toggle("is-current", sheet.dataset.market === id))
    this.sheet = id
  }
}

// The zoom-and-pan curve between two views [x, y, width] (van Wijk and Nuij,
// "Smooth and efficient zooming and panning", 2003; d3-interpolate's
// interpolateZoom). `length` is the curve's length, in screen widths.
function zoomAndPan([x0, y0, w0], [x1, y1, w1]) {
  const dx = x1 - x0
  const dy = y1 - y0
  const distanceSquared = dx * dx + dy * dy
  let at, length

  if (distanceSquared < 1e-12) {
    length = Math.log(w1 / w0) / RHO
    at = t => [x0 + t * dx, y0 + t * dy, w0 * Math.exp(RHO * t * length)]
  } else {
    const distance = Math.sqrt(distanceSquared)
    const b0 = (w1 * w1 - w0 * w0 + RHO ** 4 * distanceSquared) / (2 * w0 * RHO ** 2 * distance)
    const b1 = (w1 * w1 - w0 * w0 - RHO ** 4 * distanceSquared) / (2 * w1 * RHO ** 2 * distance)
    const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0)
    const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1)
    length = (r1 - r0) / RHO
    at = t => {
      const s = t * length
      const u = w0 / (RHO ** 2 * distance) * (Math.cosh(r0) * Math.tanh(RHO * s + r0) - Math.sinh(r0))
      return [x0 + u * dx, y0 + u * dy, w0 * Math.cosh(r0) / Math.cosh(RHO * s + r0)]
    }
  }
  return { at, length: Math.abs(length) }
}
